/**
 * FinanceApp — Questionnaire activité/priorités
 * Affiché une seule fois, juste après l'inscription et avant le wizard de
 * configuration (Société → Facturation → Client → Lancement). Alimente :
 *  - l'adaptation légère du vocabulaire du wizard (marchandise vs service),
 *  - la checklist personnalisée du dashboard,
 *  - le regroupement de la sidebar (divulgation progressive).
 * Le type d'activité reste modifiable plus tard depuis Administration >
 * Sociétés — ce questionnaire ne fige rien à vie.
 */
import { useState } from "react";
import api from "../../services/api";

const ACTIVITY_TYPES = [
  { value: "services",     label: "Services",   desc: "Prestations, conseil, abonnements…" },
  { value: "marchandises", label: "Marchandises", desc: "Vente de produits physiques" },
  { value: "mixte",        label: "Mixte",       desc: "Services et marchandises" },
];

const PRIORITIES = [
  { value: "facturation",  label: "Facturation" },
  { value: "comptabilite", label: "Comptabilité" },
  { value: "stock",        label: "Gestion de stock" },
  { value: "achats",       label: "Achats fournisseurs" },
  { value: "factelec",     label: "Facturation électronique" },
  { value: "crm",          label: "CRM / prospection" },
];

const S = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(17,24,39,0.55)",
    zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center",
    padding: "16px", overflowY: "auto",
  },
  card: {
    background: "#ffffff", borderRadius: 24, width: "100%", maxWidth: 560,
    boxShadow: "0 25px 80px rgba(0,0,0,0.18)", padding: "36px 40px", margin: "auto",
  },
  optionCard: (active) => ({
    display: "flex", flexDirection: "column", gap: 3, padding: "14px 16px",
    borderRadius: 12, border: `2px solid ${active ? "#2563eb" : "#e5e7eb"}`,
    background: active ? "#eff6ff" : "#ffffff", cursor: "pointer", transition: "all 0.15s",
  }),
  checkCard: (active) => ({
    display: "flex", alignItems: "center", gap: 10, padding: "11px 14px",
    borderRadius: 10, border: `2px solid ${active ? "#2563eb" : "#e5e7eb"}`,
    background: active ? "#eff6ff" : "#ffffff", cursor: "pointer", transition: "all 0.15s",
  }),
  btnPrimary: { padding: "11px 28px", borderRadius: 10, border: "none", background: "#2563eb", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  error: { background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#b91c1c", marginBottom: 16 },
};

export default function ActivityQuestionnaire({ company, onComplete }) {
  const [activityType, setActivityType] = useState(null);
  const [priorities, setPriorities]     = useState([]);
  const [saving, setSaving]             = useState(false);
  const [err, setErr]                   = useState("");

  const togglePriority = (value) => {
    setPriorities(p => p.includes(value) ? p.filter(v => v !== value) : [...p, value]);
  };

  const canSubmit = !!activityType && priorities.length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true); setErr("");
    try {
      await api.onboarding.saveActivityProfile({ activity_type: activityType, priorities });
      await onComplete();
    } catch (e) {
      setErr(e.message || "Erreur lors de l'enregistrement.");
      setSaving(false);
    }
  };

  return (
    <div style={S.overlay}>
      <div style={S.card}>
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: "#111827", margin: "0 0 6px" }}>
            Bienvenue{company?.name ? `, ${company.name}` : ""} 👋
          </h2>
          <p style={{ fontSize: 14, color: "#6b7280", margin: 0, lineHeight: 1.6 }}>
            Deux questions rapides pour personnaliser votre espace de travail.
          </p>
        </div>

        {err && <div style={S.error}>{err}</div>}

        {/* Question 1 — Type d'activité */}
        <div style={{ marginBottom: 26 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 10 }}>
            Quel type d'activité exercez-vous ?
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            {ACTIVITY_TYPES.map(t => (
              <div key={t.value} style={S.optionCard(activityType === t.value)} onClick={() => setActivityType(t.value)}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{t.label}</span>
                <span style={{ fontSize: 11, color: "#6b7280" }}>{t.desc}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Question 2 — Priorités */}
        <div style={{ marginBottom: 30 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 3 }}>
            Qu'est-ce qui vous intéresse en premier ?
          </div>
          <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 10 }}>Plusieurs choix possibles</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {PRIORITIES.map(p => {
              const active = priorities.includes(p.value);
              return (
                <div key={p.value} style={S.checkCard(active)} onClick={() => togglePriority(p.value)}>
                  <div style={{
                    width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                    border: `2px solid ${active ? "#2563eb" : "#d1d5db"}`,
                    background: active ? "#2563eb" : "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {active && <span style={{ color: "#fff", fontSize: 10, fontWeight: 900 }}>✓</span>}
                  </div>
                  <span style={{ fontSize: 12.5, fontWeight: 500, color: "#111827" }}>{p.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || saving}
            style={{ ...S.btnPrimary, opacity: !canSubmit || saving ? 0.5 : 1, cursor: !canSubmit || saving ? "not-allowed" : "pointer" }}
          >
            {saving ? "Enregistrement…" : "Continuer →"}
          </button>
        </div>
      </div>
    </div>
  );
}
